import { ChevronDown, ChevronUp, Copy, Eye, EyeOff, Trash2 } from 'lucide-react';
import { rpSectionLibraryEntry, type RpSection } from '@mlm/shared';

/**
 * The page's sections, in order.
 *
 * Reordering is ↑/↓ plus Duplicate, not drag-and-drop. Three reasons: the repo
 * has no DnD library and every other reorder in the admin (storefront rows,
 * layout sections, featured packages) is ↑/↓, so drag here would make this one
 * screen the odd one out; pages run 6–12 sections, where clicking a section in
 * the preview and nudging it is quicker than dragging; and Duplicate is worth
 * more than drag to this audience — building a three-card benefits grid is
 * duplicate-and-edit.
 */
export function RpSectionOutline({
  sections,
  selectedId,
  onSelect,
  onMove,
  onDuplicate,
  onToggle,
  onRemove,
}: {
  sections: RpSection[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onMove: (id: string, dir: -1 | 1) => void;
  onDuplicate: (id: string) => void;
  onToggle: (id: string) => void;
  onRemove: (id: string) => void;
}) {
  if (sections.length === 0) {
    return (
      <p className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">
        No sections yet. Add one to start building the page.
      </p>
    );
  }
  return (
    <ul className="space-y-1">
      {sections.map((s, i) => {
        const entry = rpSectionLibraryEntry(s.type);
        const selected = s.id === selectedId;
        return (
          <li
            key={s.id}
            className={`group rounded-md border p-2 transition-colors ${
              selected ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
            }`}
          >
            <div className="flex items-center gap-1">
              <button
                type="button"
                className="min-w-0 flex-1 text-left"
                onClick={() => onSelect(s.id)}
                title={entry?.note}
              >
                <span className={`block truncate text-xs font-medium ${s.enabled ? '' : 'opacity-50'}`}>
                  {entry?.label ?? s.type}
                </span>
                <span className="block truncate text-[10px] text-muted-foreground">
                  {summarize(s)}
                </span>
              </button>
              <div className="flex shrink-0 items-center">
                <IconBtn label="Move up" disabled={i === 0} onClick={() => onMove(s.id, -1)}>
                  <ChevronUp className="h-3.5 w-3.5" />
                </IconBtn>
                <IconBtn label="Move down" disabled={i === sections.length - 1} onClick={() => onMove(s.id, 1)}>
                  <ChevronDown className="h-3.5 w-3.5" />
                </IconBtn>
                <IconBtn label="Duplicate" onClick={() => onDuplicate(s.id)}>
                  <Copy className="h-3.5 w-3.5" />
                </IconBtn>
                <IconBtn label={s.enabled ? 'Hide' : 'Show'} onClick={() => onToggle(s.id)}>
                  {s.enabled ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                </IconBtn>
                <IconBtn label="Delete" danger onClick={() => onRemove(s.id)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </IconBtn>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function IconBtn({
  label,
  onClick,
  disabled,
  danger,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={`rounded p-1 text-muted-foreground disabled:opacity-25 ${
        danger ? 'hover:text-destructive' : 'hover:text-foreground'
      }`}
    >
      {children}
    </button>
  );
}

/** A one-line hint of what's in a section, so the outline is scannable. */
function summarize(s: RpSection): string {
  const c = s.config as Record<string, unknown>;
  const first = (...keys: string[]) => {
    for (const k of keys) {
      const v = c[k];
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
    return '';
  };
  const text = first('heading', 'text', 'label', 'url', 'caption');
  if (text) return text.length > 42 ? `${text.slice(0, 42)}…` : text;
  if (Array.isArray(c.items)) return `${c.items.length} item${c.items.length === 1 ? '' : 's'}`;
  if (Array.isArray(c.buttons)) return `${c.buttons.length} button${c.buttons.length === 1 ? '' : 's'}`;
  if (typeof c.html === 'string' && c.html) {
    const plain = c.html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    return plain.length > 42 ? `${plain.slice(0, 42)}…` : plain || 'Empty';
  }
  return `#${s.id}`;
}
